import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Coins, ChevronDown, ChevronUp, Gift, MessageCircle } from "lucide-react";

const TX_LABEL = {
  signup_bonus: "Regisztrációs ajándék",
  signup_bonus_backfill: "Regisztrációs ajándék",
  credit_purchase: "Kreditcsomag vásárlás",
  credit_subscription: "Kreditcsomag előfizetés",
  listing_upload: "Hirdetés feladása",
  listing_daily: "Napi fenntartási díj",
  addon_viewer_360: "360° kiegészítő",
  addon_ai_photo_bg: "AI háttércsere",
  addon_ai_customer_support: "AI ügyfélszolgálat",
  addon_facebook_crosspost: "Facebook megosztás",
  addon_list_boost: "Kiemelés a listában",
  addon_priority_ranking: "Kiemelt rangsorolás",
  addon_meta_ads: "Meta hirdetés",
  review_reward: "Vélemény jutalom",
  referral_bonus: "Referral bónusz",
  gift_sent: "Kredit ajándék küldése",
  gift_received: "Kredit ajándék fogadása",
  voucher_redeem: "Kupon beváltás",
  signup_promo_grant: "Promó regisztráció",
  signup_promo_replenish: "Promó egyenleg feltöltés",
  detailed_stats_guarantee: "Statisztika teljesítménygarancia",
};

const FILTERS = [
  { key: "all", label: "Összes" },
  { key: "gifts", label: "Ajándékok" },
  { key: "credit", label: "Jóváírások" },
  { key: "debit", label: "Levonások" },
];

export function CreditHistory({ balance, onRefresh }) {
  const [txs, setTxs] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async (f) => {
    setLoading(true);
    try {
      const params = f && f !== "all" ? { filter: f } : {};
      const r = await api.get("/credits/transactions", { params });
      setTxs(r.data.transactions || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(filter); }, [filter]);
  useEffect(() => { if (onRefresh) load(filter); /* eslint-disable-next-line */ }, [onRefresh]);

  const COLLAPSED_LIMIT = 3;
  const visible = expanded ? txs : txs.slice(0, COLLAPSED_LIMIT);

  const renderMeta = (t) => {
    if (t.type === "gift_sent" && t.counterparty) {
      return <div className="text-[11px] text-slate-400">Címzett: <b>{t.counterparty.name || t.counterparty.email}</b>{t.meta?.message ? <> · <span className="italic">„{t.meta.message}”</span></> : null}</div>;
    }
    if (t.type === "gift_received" && t.counterparty) {
      return <div className="text-[11px] text-slate-400">Küldő: <b>{t.counterparty.name || t.counterparty.email}</b>{t.meta?.message ? <> · <span className="italic">„{t.meta.message}”</span></> : null}</div>;
    }
    if (t.type === "voucher_redeem" && t.meta?.code) {
      return <div className="text-[11px] text-slate-400">Kupon: <b className="font-mono">{t.meta.code}</b>{t.meta.note ? <> · {t.meta.note}</> : null}</div>;
    }
    return null;
  };

  return (
    <div data-testid="credit-history" className="glass rounded-3xl p-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Coins className="w-5 h-5 text-electric" strokeWidth={1.5} />
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-400">Egyenleged</div>
            <div className="font-heading text-2xl" data-testid="credit-balance">{balance ?? 0} kredit</div>
          </div>
        </div>
        <a href="/kreditek" data-testid="topup-credits" className="px-4 py-2 rounded-full bg-electric hover:bg-electric-light text-white text-sm">Kredit vásárlása</a>
      </div>

      <div className="mt-5 border-t border-white/5 pt-4">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div className="text-xs uppercase tracking-wide text-slate-400">Levonások és jóváírások</div>
          <div className="inline-flex gap-1" data-testid="credit-history-filters">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                data-testid={`credit-filter-${f.key}`}
                onClick={() => { setFilter(f.key); setExpanded(false); }}
                className={`px-3 py-1 rounded-full text-[11px] tracking-wide transition-all ${filter === f.key ? "bg-electric text-white" : "bg-white/[0.03] text-slate-400 hover:bg-white/[0.06]"}`}
              >
                {f.key === "gifts" && <Gift className="inline w-3 h-3 mr-1 -mt-0.5" />}
                {f.label}
              </button>
            ))}
          </div>
        </div>
        {loading ? (
          <div className="text-sm text-slate-500">Betöltés…</div>
        ) : txs.length === 0 ? (
          <div className="text-sm text-slate-500">Nincs a szűrésnek megfelelő tranzakció.</div>
        ) : (
          <>
            <div className="space-y-1">
              {visible.map((t) => (
                <div key={t.id} data-testid={`credit-tx-${t.id}`} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0 text-sm">
                  <div className="min-w-0 flex-1">
                    <div className="truncate flex items-center gap-1.5">
                      {t.type.startsWith("gift_") && <Gift className="w-3.5 h-3.5 text-emerald-400 shrink-0" />}
                      {t.meta?.message && <MessageCircle className="w-3 h-3 text-slate-500 shrink-0" />}
                      <span>{TX_LABEL[t.type] || t.type}</span>
                    </div>
                    {renderMeta(t)}
                    <div className="text-[11px] text-slate-500">{new Date(t.created_at).toLocaleString("hu-HU")}</div>
                  </div>
                  <div className={`font-mono font-medium ${t.amount >= 0 ? "text-emerald-400" : "text-red-300"}`}>
                    {t.amount >= 0 ? "+" : ""}{t.amount}
                  </div>
                </div>
              ))}
            </div>
            {txs.length > COLLAPSED_LIMIT && (
              <button
                data-testid="credit-history-toggle"
                onClick={() => setExpanded((e) => !e)}
                className="mt-3 inline-flex items-center gap-1 text-xs text-electric hover:text-electric-light"
              >
                {expanded ? <><ChevronUp className="w-3.5 h-3.5" /> Kevesebb</> : <><ChevronDown className="w-3.5 h-3.5" /> Az összes {txs.length} tranzakció</>}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
